import axios from "axios";
import { ISignal, SignalStats, SignalFilters } from "./signal";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

// Get all signals
export const getSignals = async (filters?: SignalFilters & {
    page?: number;
    limit?: number;
    hasLinkedIn?: string;
    minReviews?: string;
    location?: string;
    detectionDate?: string;
    from?: string;
    to?: string;
    address?: string;
    founderEstablishmentsCount?: string;
    establishmentNumber?: string;
    contactFilters?: string;
    networkStatus?: string;
    establishmentType?: string;
    concept?: string;
    minRating?: string;
}) => {
    const searchParams = new URLSearchParams();
    
    if (filters?.eventType) searchParams.append("eventType", filters.eventType);
    if (filters?.importance) searchParams.append("importance", filters.importance);
    if (filters?.company) searchParams.append("company", filters.company);
    if (filters?.tags) filters.tags.forEach(tag => searchParams.append("tags", tag));
    if (filters?.minAmount) searchParams.append("minAmount", String(filters.minAmount));
    if (filters?.search) searchParams.append("search", filters.search);
    if (filters?.isActive !== undefined && filters?.isActive !== 'all') {
        searchParams.append("isActive", String(filters.isActive));
    }
    if (filters?.sortBy) searchParams.append("sortBy", filters.sortBy);
    if (filters?.order) searchParams.append("order", filters.order);
    if (filters?.page) searchParams.append("page", String(filters.page));
    if (filters?.limit) searchParams.append("limit", String(filters.limit));
    if (filters?.hasLinkedIn) searchParams.append("hasLinkedIn", filters.hasLinkedIn);
    if (filters?.minReviews) searchParams.append("minReviews", filters.minReviews);
    if (filters?.location) searchParams.append("location", filters.location);
    if (filters?.minRating) searchParams.append("minRating", filters.minRating);
    
    // New filters
    if (filters?.detectionDate) searchParams.append("detectionDate", filters.detectionDate);
    if (filters?.from) searchParams.append("from", filters.from);
    if (filters?.to) searchParams.append("to", filters.to);
    if (filters?.address) searchParams.append("address", filters.address);
    // Backwards-compatible: support both founderEstablishmentsCount and establishmentNumber
    if (filters?.founderEstablishmentsCount) searchParams.append("founderEstablishmentsCount", filters.founderEstablishmentsCount);
    if (filters?.establishmentNumber) searchParams.append("establishmentNumber", filters.establishmentNumber);
    if (filters?.contactFilters) searchParams.append("contactFilters", filters.contactFilters);
    if (filters?.networkStatus) searchParams.append("networkStatus", filters.networkStatus);
    if (filters?.establishmentType) searchParams.append("establishmentType", filters.establishmentType);
    if (filters?.concept) searchParams.append("concept", filters.concept);
    
    const url = `/signals${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    
    const response = await axios.get(url, config());
    return response.data;
};

export interface UseCaseCounts {
    multi_site_5plus: number;
    multi_site_2_4: number;
    restaurant_opening: number;
    opening_with_email: number;
    opening_with_linkedin: number;
    owner_change: number;
    franchise_signing: number;
    hygiene_control: number;
}

export const getUseCaseCounts = async (
    from?: string,
    to?: string,
    categories?: string[],
    subCategories?: string[],
): Promise<UseCaseCounts> => {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (categories?.length) params.append("categories", categories.join(","));
    if (subCategories?.length) params.append("subCategories", subCategories.join(","));
    const response = await axios.get(`/signals/use-case-counts?${params.toString()}`, config());
    return response.data;
};

export const getEstablishmentUseCaseCounts = async (
    from?: string,
    to?: string,
    categories?: string[],
    subCategories?: string[],
): Promise<UseCaseCounts> => {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (categories?.length) params.append("categories", categories.join(","));
    if (subCategories?.length) params.append("subCategories", subCategories.join(","));
    const response = await axios.get(`/establishments/use-case-counts?${params.toString()}`, config());
    return response.data;
};

export interface SegmentChannelBreakdown {
    email: number;
    linkedin: number;
    phone: number;
}

export interface SegmentStat {
    count: number;
    channels: SegmentChannelBreakdown;
    lastUpdatedAt?: string | null;
    trend?: { value: number; label: string };
}

export type SegmentId = keyof UseCaseCounts | "equipment_renewal_5_7"
    | "furniture_renovation" | "kitchen_equipment_install" | "used_oil_collection" | "first_supplier_contact";

const segmentParams = (from?: string, to?: string, categories?: string[], subCategories?: string[]) => {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (categories?.length) params.append("categories", categories.join(","));
    if (subCategories?.length) params.append("subCategories", subCategories.join(","));
    return params.toString();
};

// One request per segment card — they run in parallel and each is cached server-side on
// its own key, so a slow segment never holds up the rest of the panel.
export const getSegmentStat = async (
    segment: SegmentId,
    from?: string,
    to?: string,
    categories?: string[],
    subCategories?: string[],
): Promise<SegmentStat> => {
    const qs = segmentParams(from, to, categories, subCategories);
    const response = await axios.get(`/establishments/segment-stats/${segment}?${qs}`, config());
    return response.data;
};

export const getSegmentTotalTracked = async (
    from?: string,
    to?: string,
    categories?: string[],
    subCategories?: string[],
): Promise<{ totalTracked: number }> => {
    const qs = segmentParams(from, to, categories, subCategories);
    const response = await axios.get(`/establishments/segment-stats/total-tracked?${qs}`, config());
    return response.data;
};

// Get a specific signal
export const getSignal = async (id: string): Promise<ISignal> => {
    const response = await axios.get(`/signals/${id}`, config());
    return response.data;
};

// Create a new signal
export const createSignal = async (signal: Partial<ISignal>): Promise<ISignal> => {
    const response = await axios.post(`/signals`, signal, config());
    return response.data;
};

// Update a signal
export const updateSignal = async (id: string, signal: Partial<ISignal>): Promise<ISignal> => {
    const response = await axios.put(`/signals/${id}`, signal, config());
    return response.data;
};

// Delete a signal
export const deleteSignal = async (id: string) => {
    const response = await axios.delete(`/signals/${id}`, config());
    return response.data;
};

// Mark signal as viewed
export const markSignalAsViewed = async (id: string) => {
    const response = await axios.post(`/signals/${id}/view`, {}, config());
    return response.data;
};

// Get unique tags from signals
export const getUniqueTags = async (): Promise<{ categories: string[]; subCategories: string[] }> => {
    const response = await axios.get(`/signals/tags/unique`, config());
    return response.data;
};

// Save/bookmark signal
export const toggleSaveSignal = async (id: string): Promise<{ saved: boolean; signal: ISignal }> => {
    const response = await axios.post(`/signals/${id}/save`, {}, config());
    return response.data;
};

// Get user's saved signals
export const getSavedSignals = async (params?: { page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", String(params.page));
    if (params?.limit) searchParams.append("limit", String(params.limit));
    
    const url = `/signals/user/saved${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    const response = await axios.get(url, config());
    return response.data;
};

// Get signal statistics
export const getSignalStats = async (): Promise<SignalStats> => {
    const response = await axios.get(`/signals/stats/summary`, config());
    return response.data;
};

// Batch create signals
export const batchCreateSignals = async (signals: Partial<ISignal>[]) => {
    const response = await axios.post(`/signals/batch/create`, { signals }, config());
    return response.data;
};

// Add signal to Kanban as a prospect
export const addSignalToKanban = async (id: string): Promise<{ message: string; prospect: any; alreadyExists: boolean }> => {
    const response = await axios.post(`/signals/${id}/add-to-kanban`, {}, config());
    return response.data;
};

// Rate a signal (1-5 stars)
export const rateSignal = async (id: string, rating: number): Promise<{ message: string; averageRating: number; ratingCount: number; userRating: number }> => {
    const response = await axios.post(`/signals/${id}/rate`, { rating }, config());
    return response.data;
};

// Get signal ratings
export const getSignalRatings = async (id: string): Promise<{ averageRating: number; ratingCount: number; userRating?: number | null; ratings?: any[] }> => {
    const response = await axios.get(`/signals/${id}/ratings`, config());
    return response.data;
};

export const getSignalRatingsAdmin = async (id: string): Promise<{ averageRating: number; ratingCount: number; userRating?: number | null; ratings?: any[] }> => {
    const response = await axios.get(`/signals/${id}/ratings-admin`, config());
    return response.data;
};

// Delete a user's rating (admin only)
export const deleteSignalRating = async (signalId: string, userId: string): Promise<{ message: string; averageRating: number; ratingCount: number }> => {
    const response = await axios.delete(`/signals/${signalId}/ratings/${userId}`, config());
    return response.data;
};

// Log a search query
export const logSearch = async (searchTerm: string, context: string = 'signals', resultCount: number = 0) => {
    const response = await axios.post(`/search-logs`, { 
        searchTerm, 
        context, 
        resultCount 
    }, config());
    return response.data;
};

// Get all searches by user (admin only)
export const getSearchesByUser = async (days: number = 30) => {
    const response = await axios.get(`/search-logs/admin/by-user?days=${days}`, config());
    return response.data;
};
